import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import MovieCard from "@/components/feature/MovieCard";
import FilterBar, { type SortKey } from "@/pages/category/components/FilterBar";
import { CATEGORIES, getCategoryMeta, getByType } from "@/lib/catalog";
import type { MovieType } from "@/types/movie";

const PAGE_SIZE = 9;

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const meta = getCategoryMeta(type ?? "");

  const [region, setRegion] = useState("all");
  const [year, setYear] = useState("all");
  const [genre, setGenre] = useState("all");
  const [sort, setSort] = useState<SortKey>("latest");
  const [visible, setVisible] = useState(PAGE_SIZE);

  const validType: MovieType = meta ? meta.key : "movie";
  const list = useMemo(() => getByType(validType), [validType]);

  const regions = useMemo(
    () => Array.from(new Set(list.map((m) => m.region))),
    [list]
  );
  const years = useMemo(
    () => Array.from(new Set(list.map((m) => m.year))).sort((a, b) => b - a),
    [list]
  );
  const genres = useMemo(
    () => Array.from(new Set(list.flatMap((m) => m.genres))),
    [list]
  );

  const filtered = useMemo(() => {
    const result = list.filter(
      (m) =>
        (region === "all" || m.region === region) &&
        (year === "all" || String(m.year) === year) &&
        (genre === "all" || m.genres.includes(genre))
    );
    return result.sort((a, b) => {
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "hot") return b.views - a.views;
      return a.updatedAt < b.updatedAt ? 1 : -1;
    });
  }, [list, region, year, genre, sort]);

  const shown = filtered.slice(0, visible);

  const resetFilters = () => {
    setRegion("all");
    setYear("all");
    setGenre("all");
    setSort("latest");
    setVisible(PAGE_SIZE);
  };

  const total = list.length;

  useDocumentMeta(
    `${meta ? meta.label : "影视"}频道 - 亚洲视频一区`,
    meta
      ? `${meta.description} 亚洲视频一区${meta.label}频道支持按地区、年份、类型筛选，快速找到你想看的内容。`
      : "亚洲视频一区影视分类频道，支持筛选与排序，快速找到你想看的内容。",
    `亚洲视频一区,${meta ? meta.label : "影视"},在线播放,高清影视,影视资源`
  );

  return (
    <div className="w-full px-4 md:px-8 lg:px-12 py-6 md:py-10">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-5">
        <Link to="/" className="hover:text-primary-500 transition-colors">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">{meta ? meta.label : "全部影视"}</span>
      </nav>

      {/* Title */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-foreground-950">
            {meta ? meta.label : "全部影视"}频道
          </h1>
          <p className="mt-2 text-sm text-foreground-600 max-w-2xl leading-relaxed">
            {meta ? meta.description : "浏览全部影视内容。"}
          </p>
        </div>
        <p className="text-sm text-foreground-500 shrink-0">
          共 <span className="text-primary-500 font-heading font-bold">{filtered.length}</span> 部结果
        </p>
      </div>

      {/* Category switch */}
      <div className="flex flex-wrap gap-2 mb-6">
        {CATEGORIES.map((cat) => (
          <Link
            key={cat.key}
            to={`/category/${cat.key}`}
            className={`px-4 py-2 rounded-md text-sm whitespace-nowrap transition-colors ${
              cat.key === validType
                ? "bg-primary-500 text-background-50 font-medium"
                : "bg-background-200/70 text-foreground-700 hover:text-foreground-950 hover:bg-background-300"
            }`}
          >
            {cat.label}
          </Link>
        ))}
      </div>

      <FilterBar
        regions={regions}
        years={years}
        genres={genres}
        region={region}
        year={year}
        genre={genre}
        sort={sort}
        onRegionChange={(v) => {
          setRegion(v);
          setVisible(PAGE_SIZE);
        }}
        onYearChange={(v) => {
          setYear(v);
          setVisible(PAGE_SIZE);
        }}
        onGenreChange={(v) => {
          setGenre(v);
          setVisible(PAGE_SIZE);
        }}
        onSortChange={(v) => {
          setSort(v);
          setVisible(PAGE_SIZE);
        }}
        onReset={resetFilters}
      />

      {/* Results */}
      {shown.length > 0 ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-5 mt-6">
            {shown.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
          {visible < filtered.length ? (
            <div className="flex justify-center mt-10">
              <button
                type="button"
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                className="inline-flex items-center gap-2 px-8 py-3 rounded-md bg-background-200/70 text-foreground-900 text-sm font-semibold hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line text-lg" />
                加载更多（剩余 {filtered.length - visible} 部）
              </button>
            </div>
          ) : (
            <p className="text-center text-sm text-foreground-500 mt-10">
              已展示全部 {filtered.length} 部内容
            </p>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center text-center py-20 rounded-lg bg-background-100 border border-background-300/50 mt-6">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 mb-4">
            <i className="ri-emotion-sad-line text-3xl" />
          </span>
          <h3 className="font-heading text-lg font-semibold text-foreground-900">
            没有找到符合条件的内容
          </h3>
          <p className="mt-2 text-sm text-foreground-600 max-w-md">
            试试调整地区、年份或类型筛选条件，或一键重置查看全部{meta ? meta.label : "影视"}。
          </p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line" />
            重置筛选条件
          </button>
        </div>
      )}

      <p className="text-center text-xs text-foreground-500 mt-8">
        本页内容均为演示数据，仅供界面与功能展示。
      </p>
    </div>
  );
}